import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Mail, Search, Loader2, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABELS = {
  temp_password: { label: "Bienvenue / accès", bg: "#FFF5F6", fg: "#E32636" },
  password_reset: { label: "Reset mot de passe", bg: "#EFF4FF", fg: "#2F5FD0" },
  debt_reminder: { label: "Relance dette", bg: "#FFF7DB", fg: "#8A6D00" },
  monthly_recap: { label: "Récap mensuel", bg: "#E7F6EE", fg: "#009E60" },
};

/** Journal des emails Resend : statut de chaque envoi + relance en un clic. */
export const EmailLogsPanel = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | sent | failed
  const [busyId, setBusyId] = useState(null);

  const load = () => apiClient.get("/admin/email-logs")
    .then(({ data }) => setLogs(data))
    .catch((err) => toast.error(formatApiError(err)));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!logs) return [];
    const s = q.trim().toLowerCase();
    return logs.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!s) return true;
      return (l.email || "").toLowerCase().includes(s)
        || (l.user_name || "").toLowerCase().includes(s)
        || (KIND_LABELS[l.kind]?.label || l.kind || "").toLowerCase().includes(s);
    });
  }, [logs, q, statusFilter]);

  const resend = async (l) => {
    const isDebt = l.kind === "debt_reminder";
    const msg = isDebt
      ? `Renvoyer un email de relance de dette à ${l.user_name || l.email} ?`
      : `Régénérer un mot de passe provisoire (valable 24 h) et le renvoyer par email à ${l.user_name || l.email} ?\n⚠️ L'ancien mot de passe provisoire ne fonctionnera plus.`;
    if (!confirm(msg)) return;
    setBusyId(l.log_id);
    try {
      if (isDebt) {
        await apiClient.post(`/admin/driver-debts/${l.user_id}/remind`);
      } else {
        const { data } = await apiClient.post(`/admin/users/${l.user_id}/reset-password`);
        if (!data.email_sent) throw { response: { data: { detail: data.email_error || "envoi impossible" } } };
      }
      toast.success(`Email renvoyé à ${l.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  if (!logs) return <div className="text-mute text-sm py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  const failedCount = logs.filter((l) => l.status === "failed").length;

  return (
    <div className="space-y-3" data-testid="admin-email-logs-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.searchEmails")}
            data-testid="email-logs-search"
            className="w-full bg-white border border-sand rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand-red" />
        </div>
        <div className="flex gap-1.5">
          {[["all", `${t("admin.allF")} (${logs.length})`], ["sent", t("admin.sentF")], ["failed", `${t("admin.failedF")}${failedCount ? ` (${failedCount})` : ""}`]].map(([v, lab]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              data-testid={`email-logs-filter-${v}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border ${statusFilter === v ? "bg-ink text-white border-ink" : "bg-white border-sand"}`}>
              {lab}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-sand rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Destinataire</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const kind = KIND_LABELS[l.kind] || { label: l.kind, bg: "#EEE", fg: "#666" };
              const canResend = l.user_id && ["temp_password", "password_reset", "debt_reminder"].includes(l.kind);
              return (
                <tr key={l.log_id} className="border-t border-sand/60" data-testid={`email-log-${l.log_id}`}>
                  <td className="p-3 whitespace-nowrap text-mute">
                    {l.created_at ? new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5" style={{ backgroundColor: kind.bg, color: kind.fg }}>{kind.label}</span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{l.email}</div>
                    {l.user_name && <div className="text-xs text-mute">{l.user_name}{l.user_role ? ` · ${l.user_role}` : ""}</div>}
                  </td>
                  <td className="p-3">
                    {l.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-brand-green font-semibold"><CheckCircle2 className="w-4 h-4" /> {t("admin.sentStatus")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-brand-red font-semibold" title={l.error || ""}>
                        <XCircle className="w-4 h-4" /> Échec
                        {l.error && <span className="text-xs font-normal text-mute max-w-[220px] truncate">— {l.error}</span>}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {canResend && (
                      <button onClick={() => resend(l)} disabled={busyId === l.log_id}
                        data-testid={`email-log-resend-${l.log_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink text-ink bg-white hover:bg-elevated px-3.5 py-1.5 text-xs font-semibold disabled:opacity-60">
                        {busyId === l.log_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Renvoyer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-mute" data-testid="email-logs-empty">
                <Mail className="w-6 h-6 mx-auto mb-2 opacity-40" />Aucun email ne correspond.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-mute">Les 200 derniers envois sont affichés. « Renvoyer » régénère un mot de passe provisoire (valable 24 h) pour les emails d'accès et de reset, ou renvoie la relance de dette.</p>
    </div>
  );
};
